import { create } from 'zustand'
import { API_BASE_URL } from '@/lib/config'
import { useAuthStore } from '@/stores/useAuthStore'
import type { Voicemail } from '@/types/voicemails'

/**
 * Store Zustand — état du mini-player audio des messages vocaux.
 *
 * Périmètre strict : juste l'élément <audio> en cours de lecture + progression.
 * Les listes de voicemails et les actions delete/markRead sont gérées par
 * TanStack Query (src/api/voicemails.ts).
 */

interface VoicemailPlayerState {
  playingVoicemailId: number | null
  audioElement: HTMLAudioElement | null
  audioProgress: number
  audioDuration: number

  play: (voicemail: Voicemail, companyId: number) => Promise<void>
  pause: () => void
  resume: () => void
  stop: () => void
  cleanup: () => void
}

export const useVoicemailPlayerStore = create<VoicemailPlayerState>((set, get) => ({
  playingVoicemailId: null,
  audioElement: null,
  audioProgress: 0,
  audioDuration: 0,

  play: async (voicemail, companyId) => {
    const token = useAuthStore.getState().accessToken
    if (!token) return

    // Stop l'audio en cours
    const { audioElement: current } = get()
    if (current) {
      current.pause()
      current.currentTime = 0
    }

    // L'élément <audio> ne peut pas envoyer Authorization header → token en query param
    const audioUrl = `${API_BASE_URL}/api/companies/${companyId}/voicemails/${voicemail.id}/audio/?token=${encodeURIComponent(token)}`

    const audio = new Audio(audioUrl)
    audio.addEventListener('loadedmetadata', () => set({ audioDuration: audio.duration }))
    audio.addEventListener('timeupdate', () =>
      set({ audioProgress: (audio.currentTime / audio.duration) * 100 }),
    )
    audio.addEventListener('ended', () =>
      set({ playingVoicemailId: null, audioProgress: 0 }),
    )
    audio.addEventListener('error', () => {
      console.error('[VoicemailPlayer] erreur lecture')
      set({ playingVoicemailId: null })
    })

    set({ audioElement: audio, playingVoicemailId: voicemail.id })
    await audio.play()
  },

  pause: () => {
    const { audioElement } = get()
    if (audioElement) audioElement.pause()
  },

  resume: () => {
    const { audioElement } = get()
    if (audioElement) void audioElement.play()
  },

  stop: () => {
    const { audioElement } = get()
    if (audioElement) {
      audioElement.pause()
      audioElement.currentTime = 0
      set({ playingVoicemailId: null, audioProgress: 0 })
    }
  },

  cleanup: () => {
    const { audioElement } = get()
    if (audioElement) {
      audioElement.pause()
      audioElement.currentTime = 0
    }
  },
}))
